// =============================================================================
// src/features/analytics/analytics-insights-block-c-data.ts - Bloco C,
// "Terreno de imprensa e execucao adversaria" (cards 07-09). Proveniencia:
// Diario_Anexo-CardsTaticos_2026-07-25.md linhas 321-449, portado verbatim
// em 2026-07-27.
// =============================================================================

import type { AnalyticsInsightBlock, AnalyticsInsightCard } from './analytics-insights-types';

const ANALYTICS_INSIGHT_CARD_07: AnalyticsInsightCard = {
  cardNumber: 7,
  cardTitle: 'Nenhum veículo regional tem saldo favorável ao monitorado',
  priorityTitle: 'Nenhum veículo regional tem saldo favorável',
  impactScore: 4,
  classificationLabel: 'TERRENO DE IMPRENSA',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Das 1.472 matérias da janela, <strong>113 citam algum nome da disputa</strong>. Nessas, o monitorado é o terceiro ' +
        'mais citado.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Nome', 'Matérias que o citam'],
      tableRows: [
        ['David Almeida', '<strong>52</strong>'],
        ['Roberto Cidade', '30'],
        ['<strong>Monitorado</strong>', '<strong>22</strong>'],
        ['Wilson Lima', '10'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml: 'Veículo a veículo, não há um único com saldo favorável:',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Veículo', 'Matérias', 'Cita monitorado', 'Cita David', 'Cita Cidade', 'Cita Wilson'],
      tableRows: [
        ['Radar Amazônico', '24', '<strong>0</strong>', '7', '8', '1'],
        ['Amazonas 1', '35', '4', '3', '<strong>8</strong>', '0'],
        ['BNC Amazonas', '32', '2', '<strong>8</strong>', '0', '4'],
        ['Amazonas Atual', '24', '1', '4', '0', '3'],
        ['Portal Mário Adolfo', '14', '2', '3', '1', '0'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'O fluxo nacional não compensa: dos 428 itens de veículos nacionais e institucionais, apenas 11 ' +
        'tocam a disputa — e o maior deles, com 213 matérias na janela, não cita o monitorado uma única vez.',
    },
  ],
  tacticalReadingHtml: [
    'O terreno de imprensa é adverso por ausência, não por ataque: o monitorado não está sendo atacado, ' +
      'está sendo pouco citado, o que é um problema diferente e exige resposta diferente. Um veículo ' +
      'regional com 24 matérias e zero citações não é hostil — é um canal onde a operação não tem entrada. ' +
      'A leitura que o número esconde: como o fluxo nacional não toca a praça, o único terreno que existe ' +
      'é o regional, e nele a assimetria é estrutural e não episódica. O CARD 08 mostra que o mesmo padrão ' +
      'se repete fora da imprensa, no conteúdo de terceiros — dois terrenos independentes com o mesmo ' +
      'resultado.',
  ],
  actionCallHtml:
    'Tratar o Radar Amazônico e o Amazonas 1 como alvos nominais de relacionamento nas próximas duas ' +
    'semanas: são os dois com maior volume e pior saldo, e a assimetria neles é de acesso, não de ' +
    'enquadre. Medir por citação, não por publicação favorável — o problema atual é existir.',
  confidenceLine:
    'ALTA — validado contra filtro de nome frouxo: a contagem usa nome completo, nunca sobrenome isolado',
  natureLine: 'estrutural',
  baseLine: '1.472 matérias, 113 citando a disputa, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 08/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva estável',
};

const ANALYTICS_INSIGHT_CARD_08: AnalyticsInsightCard = {
  cardNumber: 8,
  cardTitle: 'É quem menos existe no conteúdo que os outros produzem',
  priorityTitle: 'É quem menos existe no conteúdo dos outros',
  impactScore: 4,
  classificationLabel: 'AMEAÇA ESTRUTURAL',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Em 673 publicações de perfis que não são de nenhum ator da disputa, a presença espontânea de cada ' +
        'nome é desigual, e a desigualdade é maior em alcance do que em contagem.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Ator', 'Peças de terceiros', 'Perfis distintos que o citam', 'Visualizações dessas peças'],
      tableRows: [
        ['David Almeida', '<strong>55</strong>', '<strong>23</strong>', '<strong>378.664</strong>'],
        ['Roberto Cidade', '27', '17', '51.239'],
        ['Wilson Lima', '16', '10', '88.210'],
        ['<strong>Omar Aziz</strong>', '<strong>14</strong>', '<strong>9</strong>', '<strong>6.293</strong>'],
        ['Serafim Corrêa', '10', '7', '4.190'],
        ['Eduardo Braga', '9', '8', '1.459'],
        ['Maria do Carmo Seffair', '8', '5', '1.111'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'A razão em peças é de 4,1 para 1 contra o monitorado. Em alcance, é de <strong>60,2 para 1</strong>. Os dois ' +
        'métodos de contagem — nome completo e lista de apelidos — produzem a mesma ordem.',
    },
  ],
  tacticalReadingHtml: [
    'O achado sobrevive ao teste que normalmente o derrubaria. O monitorado tem a <strong>maior</strong> lista de ' +
      'formas de ser nomeado entre todos os atores — seis, contra duas ou três dos demais —, ou seja, tem a ' +
      'maior vantagem de captura da base e ainda assim é o que menos aparece. A vantagem joga contra ele, e ' +
      'é isso que leva a confiança a ALTA. A consequência não óbvia está na diferença entre as duas razões: ' +
      '4,1 para 1 em peças e 60,2 para 1 em alcance significa que o monitorado não é apenas menos citado — ' +
      'é citado nos perfis de menor audiência. Ganhar volume de menção sem ganhar perfil grande não corrige ' +
      'o problema.',
  ],
  actionCallHtml:
    'Priorizar presença nos poucos perfis de terceiros com alcance real que já citam a disputa, em vez ' +
    'de ampliar o número de perfis pequenos. Alvo de duas semanas: existir nos perfis que hoje carregam ' +
    'David Almeida com seis dígitos de audiência.',
  confidenceLine:
    'ALTA — validado contra assimetria de cadastro (V5): a lista de apelidos do monitorado é a maior da base e o ' +
    'resultado permanece; e contra divergência de método, com nome completo e apelidos concordando na ordem',
  natureLine: 'estrutural',
  baseLine: '673 publicações de terceiros, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 08/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva instrumental, tese e impacto estáveis',
};

const ANALYTICS_INSIGHT_CARD_09: AnalyticsInsightCard = {
  cardNumber: 9,
  cardTitle: 'Um campo tem slogan padronizado com número de urna, e ele já saiu do próprio campo',
  priorityTitle: 'Um campo tem slogan padronizado com número de urna',
  impactScore: 2,
  classificationLabel: 'VIGILÂNCIA',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Procurando texto idêntico escrito por autores diferentes, um padrão se destaca por aparecer fora dos ' +
        'perfis do próprio campo.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Texto', 'Repetições', 'Autores distintos', 'Perfis onde aparece'],
      tableRows: [
        ['<strong>"maria do carmo governadora"</strong>', '8', '7', '<strong>5</strong>'],
        ['<strong>"maria do carmo 22"</strong>', '7', '6', '<strong>4</strong>'],
        ['"meu governador"', '9', '9', '4'],
        ['"david almeida governador"', '5', '4', '2'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'As duas primeiras linhas circulam em perfis que não são do campo dela — incluindo uma página de ' +
        'alerta urbano e um perfil de agremiação. As demais expressões repetidas da janela são elogio ' +
        'genérico, sem número de urna e concentradas nos perfis do próprio ator.',
    },
  ],
  tacticalReadingHtml: [
    'O que separa operação de entusiasmo não é a repetição — é o número de perfis distintos onde o mesmo ' +
      'texto aparece. Elogio genérico repetido no próprio perfil é audiência engajada; slogan com número de ' +
      'urna aparecendo em página de terceiro é mensagem distribuída. O volume ainda é baixo e não muda a ' +
      'disputa hoje, e é por isso que o card é de vigilância e não de ameaça: o que se acompanha é a ' +
      'tendência, não o número atual. Se o mesmo padrão aparecer na próxima edição com mais perfis de ' +
      'terceiros, deixa de ser vigilância.',
  ],
  actionCallHtml:
    'Não reagir publicamente: reagir a operação pequena é o que a torna grande. Manter a contagem de ' +
    'perfis distintos em observação e reavaliar na próxima edição; a decisão de agir depende de a ' +
    'contagem crescer, não de ela existir.',
  confidenceLine:
    'MÉDIA — amostra pequena (8 e 7 ocorrências), abaixo do limiar de 20 que autorizaria confiança ALTA; a ' +
    'limitação vale para a leitura de tendência, não para o fato da circulação em perfis de terceiros',
  natureLine: 'conjuntural',
  baseLine: '6.664 comentários da janela, 17 a 24/07',
  validityLine: 'revalidar quinzenalmente · ação até 24/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva estável',
};

export const ANALYTICS_INSIGHTS_BLOCK_C: AnalyticsInsightBlock = {
  blockId: 'C',
  blockTitle: 'Bloco C — Terreno de imprensa e execução adversária',
  blockCards: [ANALYTICS_INSIGHT_CARD_07, ANALYTICS_INSIGHT_CARD_08, ANALYTICS_INSIGHT_CARD_09],
};
